import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { LoadingState } from '@/components/composites/states'
import { useDeskMeta, useSaveDeskSettings, type DeskSettings } from './api'
import { useMembersById } from './parts'

const NONE = '__none__'

/**
 * Support settings, shown on the main Settings page (Settings → Support desk): who helpdesk is,
 * who reminders escalate to, where posts go, and whether the desk may message customers.
 */
export function SupportDeskSettings() {
  const meta = useDeskMeta()
  if (!meta.data) return <LoadingState />
  // Keyed on the loaded settings so a save that changes them re-seeds the form from the server.
  return <SettingsForm key={JSON.stringify(meta.data.settings)} initial={meta.data.settings} />
}

function SettingsForm({ initial }: { initial: DeskSettings }) {
  const save = useSaveDeskSettings()
  const { list } = useMembersById()
  const [draft, setDraft] = useState<DeskSettings>(initial)
  const set = (patch: Partial<DeskSettings>) => setDraft((d) => ({ ...d, ...patch }))

  const person = (label: string, value: string | null, onChange: (v: string | null) => void) => (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      <Select value={value ?? NONE} onValueChange={(v) => onChange(v === NONE ? null : v)}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>Nobody</SelectItem>
          {list.map((m) => <SelectItem key={m.id} value={m.id}>{m.displayName}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  )

  return (
    <div className="grid max-w-2xl gap-4 rounded-lg border border-border bg-card p-5">
          <div className="grid gap-4">
            <div className="grid gap-1.5">
              <Label>Helpdesk people</Label>
              <div className="grid max-h-44 gap-1 overflow-y-auto rounded-md border border-border p-2 sm:grid-cols-2">
                {list.map((m) => (
                  <label key={m.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={draft.helpdeskMemberIds.includes(m.id)}
                      onCheckedChange={(v) => set({ helpdeskMemberIds: v === true ? [...draft.helpdeskMemberIds, m.id] : draft.helpdeskMemberIds.filter((id) => id !== m.id) })}
                    />
                    {m.displayName}
                  </label>
                ))}
              </div>
            </div>
            {draft.helpdeskMemberIds.length ? (
              <div className="grid gap-1.5">
                <Label>Away (leave, sick, off shift)</Label>
                <div className="flex flex-wrap gap-x-5 gap-y-1 rounded-md border border-border p-2">
                  {draft.helpdeskMemberIds.map((id) => {
                    const m = list.find((x) => x.id === id)
                    const away = draft.helpdeskAwayIds ?? []
                    return (
                      <label key={id} className="flex items-center gap-2 text-sm">
                        <Checkbox checked={away.includes(id)} onCheckedChange={(v) => set({ helpdeskAwayIds: v === true ? [...away, id] : away.filter((x) => x !== id) })} />
                        {m?.displayName ?? id}
                      </label>
                    )
                  })}
                </div>
                <p className="text-tiny text-text-muted">A ticket's alerts go to whoever has its conversation in Intercom. While they are away, the other helpdesk person gets them; a missed deadline always reaches both.</p>
              </div>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              {person('Helpdesk lead', draft.helpdeskLeadId, (v) => set({ helpdeskLeadId: v }))}
              {person('Head of escalation (defaults to the CEO)', draft.escalationHeadId, (v) => set({ escalationHeadId: v }))}
              {person('Product & Solutions lead (told about every escalation)', draft.productLeadId, (v) => set({ productLeadId: v }))}
              <div className="grid gap-1.5">
                <Label htmlFor="sd-hd-ch">Helpdesk Slack channel id (optional)</Label>
                <Input id="sd-hd-ch" value={draft.helpdeskChannelId ?? ''} onChange={(e) => set({ helpdeskChannelId: e.target.value.trim() || null })} placeholder="Empty: helpdesk gets direct messages" />
              </div>
            </div>
            <label className="flex items-start gap-3 rounded-md border border-border p-3 text-sm">
              <Switch checked={draft.teamAlertsInSla !== false} onCheckedChange={(v) => set({ teamAlertsInSla: v })} />
              <span><strong>Post team ticket alerts in #support-sla</strong>, each in the ticket's own thread, so everyone sees every team's tickets. Off: each team's on-call channel (set under Support teams).</span>
            </label>
            <p className="text-tiny text-text-muted">The CEO is the head of escalation unless someone else is picked above.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="sd-sla-ch">#support-sla channel id</Label>
                <Input id="sd-sla-ch" value={draft.slaChannelId ?? ''} onChange={(e) => set({ slaChannelId: e.target.value.trim() || null })} placeholder="C0…" />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="sd-war-ch">#support-incidentwarroom channel id</Label>
                <Input id="sd-war-ch" value={draft.warRoomChannelId ?? ''} onChange={(e) => set({ warRoomChannelId: e.target.value.trim() || null })} placeholder="C0…" />
              </div>
            </div>
            <label className="flex items-start gap-3 rounded-md border border-border p-3 text-sm">
              <Switch checked={draft.slackEnabled} onCheckedChange={(v) => set({ slackEnabled: v })} />
              <span><strong>Send Slack reminders and posts from this environment.</strong> On in production; off on staging so testing never messages anyone.</span>
            </label>
            <label className="flex items-start gap-3 rounded-md border border-border p-3 text-sm">
              <Switch checked={draft.autoReplies} onCheckedChange={(v) => set({ autoReplies: v })} />
              <span><strong>Send customer updates through Intercom automatically</strong> (received, triaged, working on it, restored, fixed, questions). Needs the Intercom admin id they are sent as.</span>
            </label>
            <div className="grid gap-1.5">
              <Label htmlFor="sd-admin">Intercom admin id to send as</Label>
              <Input id="sd-admin" value={draft.intercomAdminId ?? ''} onChange={(e) => set({ intercomAdminId: e.target.value.trim() || null })} placeholder="e.g. 7012345" />
            </div>
          </div>
        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button variant="outline" disabled={save.isPending || JSON.stringify(draft) === JSON.stringify(initial)} onClick={() => setDraft(initial)}>Discard changes</Button>
          <Button
            disabled={save.isPending}
            onClick={() =>
              save.mutate(
                {
                  helpdeskMemberIds: draft.helpdeskMemberIds,
                  helpdeskAwayIds: (draft.helpdeskAwayIds ?? []).filter((id) => draft.helpdeskMemberIds.includes(id)),
                  teamAlertsInSla: draft.teamAlertsInSla !== false,
                  helpdeskLeadId: draft.helpdeskLeadId,
                  escalationHeadId: draft.escalationHeadId,
                  productLeadId: draft.productLeadId,
                  helpdeskChannelId: draft.helpdeskChannelId,
                  slaChannelId: draft.slaChannelId,
                  warRoomChannelId: draft.warRoomChannelId,
                  slackEnabled: draft.slackEnabled,
                  autoReplies: draft.autoReplies,
                  intercomAdminId: draft.intercomAdminId,
                },
              )
            }
          >
            {save.isPending ? 'Saving…' : 'Save settings'}
          </Button>
        </div>
    </div>
  )
}
